import { AxiosError } from 'axios';

import { captureException } from '~/utils/sentry';

import { refreshAccessToken } from './refreshAccessToken';

type CurriedAsyncFn<T extends any[], R> = (...args: T) => Promise<R>;

function shouldLogErrorToSentry(error: any): boolean {
  // 5xx responses are intentionally NOT captured here — Sentry's
  // httpClientIntegration (configured in src/index.tsx) auto-captures every
  // 500-599 response with the full request/response context. Capturing here
  // too just produces duplicate events.
  //
  // What this function captures is the failure modes the integration *can't*
  // see — failures that never produce an HTTP response at all (timeouts,
  // dropped connections, CORS preflight failures).

  if (
    error.code === AxiosError.ECONNABORTED ||
    error.code === AxiosError.ETIMEDOUT
  ) {
    return true;
  }
  if (error.message?.includes('timeout')) return true;

  if (error.code === 'ECONNRESET' || error.code === 'ECONNREFUSED') return true;
  if (error.code === 'ENOTFOUND' || error.code === 'EHOSTUNREACH') return true;

  if (
    typeof error.message === 'string' &&
    error.message.toLowerCase().includes('cors')
  ) {
    return true;
  }

  return false;
}

// Helper function to categorize error type
function getErrorType(error: any): string {
  if (
    error.code === AxiosError.ECONNABORTED ||
    error.code === AxiosError.ETIMEDOUT ||
    error.message?.includes('timeout')
  ) {
    return 'timeout';
  }
  if (error.message?.toLowerCase().includes('cors')) {
    return 'cors_error';
  }
  if (error.code === 'ECONNRESET') return 'connection_reset';
  if (error.code === 'ECONNREFUSED') return 'connection_refused';
  if (error.code === 'ENOTFOUND') return 'dns_lookup_failed';
  if (error.code === 'EHOSTUNREACH') return 'host_unreachable';

  if (error.code === AxiosError.ERR_BAD_REQUEST) return 'bad_request';
  if (error.code === AxiosError.ERR_INVALID_URL) return 'invalid_url';
  if (error.response?.status) return `http_${error.response.status}`;
  return 'unknown';
}

export function with401Retry<T extends any[], R>(method: CurriedAsyncFn<T, R>) {
  const wrapped = (...args: T) => {
    // put try/catch into a function so that
    // we can recursively make the same request if we catch
    const makeRequest = async (wasRetrying = false): Promise<R> => {
      try {
        return await method(...args);
      } catch (error: any) {
        if (error.response?.status === 401) {
          if (wasRetrying) {
            throw error;
          }

          await refreshAccessToken();
          // if someone can reach this point, we assume there is a valid access_token

          return makeRequest(true);
        }

        if (shouldLogErrorToSentry(error)) {
          const errorType = getErrorType(error);
          captureException(`[Axios Error] - API ${errorType} error`, error, {
            errorType,
            errorCode: error.code,
            url: error.config?.url || 'unknown',
            method: error.config?.method || 'unknown',
            status: error.response?.status,
            statusText: error.response?.statusText,
          });
        }

        // TODO: EKP-1398
        // swallow other 4xx error potentially
        throw error;
      }
    };

    return makeRequest();
  };

  return wrapped;
}
